import React, { useState, useMemo } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, Typography, TextField,
  InputAdornment, TableSortLabel } from '@mui/material';
import dayjs from 'dayjs';
import { FaSearch } from 'react-icons/fa';
import { AccountingData } from '../../services/accountingService';

const MonthlyOrdersView: React.FC<{ orders: AccountingData[] }> = ({ orders }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const filteredData = useMemo(() => {
    return orders
      .filter(day => {
        const matchesSearch = dayjs(day.date.toDate()).format('DD/MM/YYYY')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        return matchesSearch;
      })
      .sort((a, b) => {
        const dateA = dayjs(a.date.toDate());
        const dateB = dayjs(b.date.toDate());
        return order === 'asc' ? dateA.diff(dateB) : dateB.diff(dateA);
      });
  }, [orders, searchTerm, order]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleRequestSort = () => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Box component="div" sx={{ width: '100%' }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Rechercher une date..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaSearch />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={handleRequestSort}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Montant Total</TableCell>
              <TableCell align="right">Base HT</TableCell>
              <TableCell align="right">Quota HT</TableCell>
              <TableCell align="right">TVA Base</TableCell>
              <TableCell align="right">TVA Quota</TableCell>
              <TableCell align="right">Espèces</TableCell>
              <TableCell align="right">CB</TableCell>
              <TableCell align="right">Tickets resto</TableCell>
              <TableCell align="right">Autres</TableCell>
              <TableCell align="right">Nb Commandes</TableCell>
              <TableCell align="right">Nb Clients</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.date.toString()} hover>
                <TableCell>{dayjs(row.date.toDate()).format('DD/MM/YYYY')}</TableCell>
                <TableCell align="right">{formatCurrency(row.totalAmount)}</TableCell>
                <TableCell align="right">{formatCurrency(row.totalBase)}</TableCell>
                <TableCell align="right">{formatCurrency(row.totalQuota)}</TableCell>
                <TableCell align="right">{formatCurrency(row.taxBase)}</TableCell>
                <TableCell align="right">{formatCurrency(row.taxQuota)}</TableCell>
                <TableCell align="right">{formatCurrency(row.paymentMethods.cash)}</TableCell>
                <TableCell align="right">{formatCurrency(row.paymentMethods.card)}</TableCell>
                <TableCell align="right">{formatCurrency(row.paymentMethods.ticket)}</TableCell>
                <TableCell align="right">{formatCurrency(row.paymentMethods.other)}</TableCell>
                <TableCell align="right">{row.ordersCount}</TableCell>
                <TableCell align="right">{row.customersCount}</TableCell>
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    Aucune donnée trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Box>
  );
};

export default MonthlyOrdersView;